import { Component, ChangeDetectionStrategy, computed, input, output, signal } from '@angular/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import type { AddButtonType, AddEvent } from '../add-buttons/add-buttons.component';
import { ADD_INDEX_OFFSET } from '../add-buttons/add-buttons.component';

import { BlockHeightResizeDirective } from '../../directives/block-height-resize.directive';

@Component({
    selector: 'web-resizable-add-buttons',
    templateUrl: './resizable-add-buttons.component.html',
    styleUrls: ['./resizable-add-buttons.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TooltipModule, BlockHeightResizeDirective],
    host: {
        '[style]': 'hostStyle()',
        '(mouseenter)': 'isHovered.set(true)',
        '(mouseleave)': 'isHovered.set(false)',
    },
})
export class ResizableAddButtonsComponent {
    /**
     * Whether the strip is shown without being hovered — the host has hover of its
     * own, see {@link isShown}. Undefined because the callers hand it a threshold
     * off a hover state that has none while nothing is hovered.
     */
    public readonly show = input<boolean | undefined>(false);
    public readonly type = input.required<AddButtonType>();
    public readonly tooltipText = input<string | null>(null);
    public readonly isFullWidthBlock = input<boolean | undefined>(false);

    /** The block's current height, which the drag handle takes as its starting point. */
    public readonly height = input<number>(0);

    public readonly addEvent = output<AddEvent>();
    public readonly resizeEvent = output<number>();

    /**
     * The two ends of a drag. The resize directive has always emitted them; until
     * now nothing listened, so a host could only see the stream of heights and had
     * to treat every pointermove as an edit of its own.
     */
    public readonly resizeStartEvent = output<void>();
    public readonly resizeEndEvent = output<void>();

    /**
     * Hover on the host, not on the strip inside it: the host is the wider
     * hit area that reaches past the block's edge, which is what makes the
     * button appear before the pointer is on it.
     */
    protected readonly isHovered = signal<boolean>(false);

    public readonly isShown = computed<boolean>(() => Boolean(this.show()) || this.isHovered());

    /** The class the stylesheet keys the strip's placement off. */
    public readonly sideClass = computed<Record<AddButtonType, boolean>>(() => ({
        left: this.type() === 'left',
        right: this.type() === 'right',
        top: this.type() === 'top',
        bottom: this.type() === 'bottom',
    }));

    /** Which edge of the block the handle drags — the one this strip sits on. */
    public readonly edge = computed<'top' | 'bottom'>(() => (this.type() === 'top' ? 'top' : 'bottom'));

    /**
     * The host is placed just outside the edge it adds on, and one of its four
     * offsets is keyed by that edge — hence a style map rather than four class
     * variants in the stylesheet.
     */
    public readonly hostStyle = computed<Record<string, string | number>>(() => {
        const type = this.type();
        const opacity = this.isShown() ? 1 : 0;

        if (type === 'left' || type === 'right') {
            return {
                [type]: this.isFullWidthBlock() ? '0' : '-19px',
                width: '20px',
                top: 0,
                bottom: 0,
                opacity,
            };
        }

        return {
            [type]: '-16px',
            height: '30px',
            left: 0,
            right: 0,
            opacity,
        };
    });

    public onClick(): void {
        const type = this.type();

        this.addEvent.emit({ type, indexOffset: ADD_INDEX_OFFSET[type] });
    }
}
